import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
  getMailConfig,
  testMailImap,
  testMailSmtp,
  updateMailConfig,
} from "../../../../api/mailConfig";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { useSkeleton } from "../../../hooks/useSkeleton";

const SECURITY_MODE_OPTIONS = [
  { value: "SslOnConnect", label: "SSL/TLS" },
  { value: "StartTls", label: "STARTTLS" },
  { value: "StartTlsWhenAvailable", label: "STARTTLS si está disponible" },
  { value: "None", label: "Sin cifrado" },
];

function getEmptyMailConfig() {
  return {
    id: null,
    test_mode: true,
    test_email: "",
    imap_server: "",
    imap_port: 993,
    imap_security_mode: "SslOnConnect",
    smtp_server: "",
    smtp_port: 587,
    smtp_security_mode: "StartTls",
    nombre_remitente: "",
    email: "",
    user_name: "",
    password: "",
    firma_html: "",
    use_imap: true,
    activo: true,
    fecha_ultima_prueba: null,
    resultado_ultima_prueba: "",
  };
}

export default function MailConfigPage() {
  const [mailConfig, setMailConfig] = useState(getEmptyMailConfig());
  const [showPassword, setShowPassword] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [openSmtpDialog, setOpenSmtpDialog] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState("");

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const accessToken = loggedUser?.accessToken;

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    getMailConfig(accessToken)
      .then((res) => {
        if (res.status === 200) {
          setMailConfig({ ...getEmptyMailConfig(), ...res.data });
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se ha podido obtener la configuración de correo.",
        });
        disableLoadingData();
      });
  }, [accessToken, disableLoadingData]);

  const handleChange = (field) => (event) => {
    const value =
      field === "imap_port" || field === "smtp_port"
        ? event.target.value === ""
          ? ""
          : Number(event.target.value)
        : field === "test_mode"
        ? event.target.value === "true"
        : event.target.value;

    setMailConfig((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const validateMailConfig = () => {
    if (!mailConfig.test_email?.trim() && mailConfig.test_mode) {
      alertError({
        error: "El email de test es obligatorio cuando el modo test está activo.",
      });
      return false;
    }

    if (!mailConfig.imap_server?.trim()) {
      alertError({ error: "El servidor IMAP es obligatorio." });
      return false;
    }

    if (!mailConfig.imap_port) {
      alertError({ error: "El puerto IMAP es obligatorio." });
      return false;
    }

    if (!mailConfig.smtp_server?.trim()) {
      alertError({ error: "El servidor SMTP es obligatorio." });
      return false;
    }

    if (!mailConfig.smtp_port) {
      alertError({ error: "El puerto SMTP es obligatorio." });
      return false;
    }

    if (!mailConfig.nombre_remitente?.trim()) {
      alertError({ error: "El nombre del remitente es obligatorio." });
      return false;
    }

    if (!mailConfig.email?.trim()) {
      alertError({ error: "El email es obligatorio." });
      return false;
    }

    if (!mailConfig.user_name?.trim()) {
      alertError({ error: "El usuario de autenticación es obligatorio." });
      return false;
    }

    if (!mailConfig.password?.trim()) {
      alertError({ error: "La contraseña del correo es obligatoria." });
      return false;
    }

    return true;
  };

  const saveMailConfig = () => {
    if (!validateMailConfig()) {
      return;
    }

    updateMailConfig(mailConfig, accessToken)
      .then((res) => {
        if (res.status === 204) {
          alertSuccess({
            title: "Guardado",
            customMessage: "Configuración de correo guardada con éxito.",
          });
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se ha podido guardar la configuración de correo.",
        });
      });
  };

  const applyTestResult = (result) => {
    setMailConfig((current) => ({
      ...current,
      fecha_ultima_prueba: result?.tested_at || current.fecha_ultima_prueba,
      resultado_ultima_prueba: `${result?.tested_protocol}: ${
        result?.success ? "OK" : "ERROR"
      } - ${result?.message || ""}`,
    }));
  };

  const handleTestImap = () => {
    if (!validateMailConfig()) {
      return;
    }

    setTestingImap(true);
    testMailImap(mailConfig, accessToken)
      .then((res) => {
        if (res.status === 200) {
          applyTestResult(res.data);
          alertSuccess({
            title: "IMAP OK",
            customMessage: res.data.message,
          });
        }
      })
      .catch((error) => {
        const result = error?.response?.data;
        if (result) {
          applyTestResult(result);
        }
        alertError({
          error,
          customMessage: result?.message || "La prueba IMAP ha fallado.",
        });
      })
      .finally(() => {
        setTestingImap(false);
      });
  };

  const handleOpenSmtpDialog = () => {
    if (!validateMailConfig()) {
      return;
    }

    setSmtpTestEmail("");
    setOpenSmtpDialog(true);
  };

  const handleTestSmtp = () => {
    if (!mailConfig.test_mode && !smtpTestEmail?.trim()) {
      return;
    }

    setTestingSmtp(true);
    testMailSmtp(
      {
        ...mailConfig,
        target_email: smtpTestEmail.trim(),
      },
      accessToken
    )
      .then((res) => {
        if (res.status === 200) {
          applyTestResult(res.data);
          alertSuccess({
            title: "SMTP OK",
            customMessage: res.data.message,
          });
          setOpenSmtpDialog(false);
        }
      })
      .catch((error) => {
        const result = error?.response?.data;
        if (result) {
          applyTestResult(result);
        }
        alertError({
          error,
          customMessage: result?.message || "La prueba SMTP ha fallado.",
        });
      })
      .finally(() => {
        setTestingSmtp(false);
      });
  };

  const isValidTestEmail =
    mailConfig.test_mode ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(smtpTestEmail?.trim() || "");

  if (isLoadingData) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title="Configuración de mail">
          <CardHeaderToolbar>
            <Button
              onClick={handleTestImap}
              variant="outlined"
              style={{ marginRight: "12px" }}
              disabled={testingImap}
            >
              {testingImap ? "Probando IMAP..." : "Probar IMAP"}
            </Button>
            <Button
              onClick={handleOpenSmtpDialog}
              variant="outlined"
              style={{ marginRight: "12px" }}
              disabled={testingSmtp}
            >
              Probar SMTP
            </Button>
            <Button onClick={saveMailConfig} variant="outlined" color="primary">
              Guardar configuración
            </Button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <div className="row">
            <div className="col-md-3">
              <TextField
                select
                fullWidth
                margin="normal"
                variant="outlined"
                label="Modo test"
                value={String(mailConfig.test_mode)}
                onChange={handleChange("test_mode")}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="true">Sí</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </TextField>
            </div>
            <div className="col-md-9">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Email test"
                value={mailConfig.test_email}
                onChange={handleChange("test_email")}
                InputLabelProps={{ shrink: true }}
                required={mailConfig.test_mode}
                helperText={
                  mailConfig.test_mode
                    ? "Con el modo test activo, todos los envíos irán siempre al email configurado aquí."
                    : ""
                }
              />
            </div>

            <div className="col-md-6">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Servidor IMAP"
                value={mailConfig.imap_server}
                onChange={handleChange("imap_server")}
                InputLabelProps={{ shrink: true }}
                required
              />
            </div>
            <div className="col-md-3">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Puerto IMAP"
                type="number"
                value={mailConfig.imap_port}
                onChange={handleChange("imap_port")}
                InputLabelProps={{ shrink: true }}
                required
              />
            </div>
            <div className="col-md-3">
              <TextField
                select
                fullWidth
                margin="normal"
                variant="outlined"
                label="Seguridad IMAP"
                value={mailConfig.imap_security_mode}
                onChange={handleChange("imap_security_mode")}
                InputLabelProps={{ shrink: true }}
              >
                {SECURITY_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Servidor SMTP"
                value={mailConfig.smtp_server}
                onChange={handleChange("smtp_server")}
                InputLabelProps={{ shrink: true }}
                required
              />
            </div>
            <div className="col-md-3">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Puerto SMTP"
                type="number"
                value={mailConfig.smtp_port}
                onChange={handleChange("smtp_port")}
                InputLabelProps={{ shrink: true }}
                required
              />
            </div>
            <div className="col-md-3">
              <TextField
                select
                fullWidth
                margin="normal"
                variant="outlined"
                label="Seguridad SMTP"
                value={mailConfig.smtp_security_mode}
                onChange={handleChange("smtp_security_mode")}
                InputLabelProps={{ shrink: true }}
              >
                {SECURITY_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Nombre remitente"
                value={mailConfig.nombre_remitente}
                onChange={handleChange("nombre_remitente")}
                InputLabelProps={{ shrink: true }}
                required
              />
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Email"
                type="email"
                value={mailConfig.email}
                onChange={handleChange("email")}
                InputLabelProps={{ shrink: true }}
                required
              />
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Usuario autenticación"
                value={mailConfig.user_name}
                onChange={handleChange("user_name")}
                InputLabelProps={{ shrink: true }}
                required
              />
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Password"
                type={showPassword ? "text" : "password"}
                value={mailConfig.password}
                onChange={handleChange("password")}
                InputLabelProps={{ shrink: true }}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((current) => !current)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Última prueba"
                value={mailConfig.fecha_ultima_prueba || ""}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
              />
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Resultado última prueba"
                value={mailConfig.resultado_ultima_prueba || ""}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
              />
            </div>
            <div className="col-md-12" style={{ marginTop: "32px" }}>
              <div
                style={{
                  marginBottom: "8px",
                  fontSize: "0.9rem",
                  color: "#7E8299",
                  fontWeight: 500,
                }}
              >
                Firma del mail
              </div>
              <div
                style={{
                  border: "1px solid #E4E6EF",
                  borderRadius: "0.42rem",
                  backgroundColor: "#FFFFFF",
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#7E8299",
                    marginBottom: "8px",
                  }}
                >
                  HTML firma
                </div>
                <textarea
                  value={mailConfig.firma_html || ""}
                  onChange={handleChange("firma_html")}
                  spellCheck={false}
                  style={{
                    width: "100%",
                    minHeight: "320px",
                    resize: "vertical",
                    overflow: "auto",
                    border: "1px solid #D1D3E0",
                    borderRadius: "0.42rem",
                    padding: "12px 14px",
                    fontFamily: "'Courier New', monospace",
                    fontSize: "0.95rem",
                    lineHeight: 1.5,
                    color: "#3F4254",
                    outline: "none",
                    backgroundColor: "#FCFCFD",
                  }}
                />
                <div
                  style={{
                    marginTop: "8px",
                    color: "#7E8299",
                    fontSize: "0.85rem",
                  }}
                >
                  Introduce aqui HTML puro. Este contenido se insertara tal cual
                  al final del mail HTML.
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <Dialog
        open={openSmtpDialog}
        onClose={() => !testingSmtp && setOpenSmtpDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Prueba SMTP</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            variant="outlined"
            label="Email de destino"
            type="email"
            value={smtpTestEmail}
            onChange={(event) => setSmtpTestEmail(event.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={
              mailConfig.test_mode
                ? "Modo test activo: se ignorará este valor y se usará el email de test configurado."
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenSmtpDialog(false)}
            disabled={testingSmtp}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleTestSmtp}
            color="primary"
            variant="outlined"
            disabled={!isValidTestEmail || testingSmtp}
          >
            {testingSmtp ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
